import personalDetails from "./personalDetails.json";
import { useEffect, useRef, useState } from "react";
import useDistrictBlocks from "../../Hooks/useDistrictBlocks";
import useBlockPanchayats from "../../Hooks/useBlockPanchayats";
import usePanchayatVillages from "../../Hooks/usePanchayatVillages";
import useDistrict from "../../Hooks/useDistrict";
import {
  handleFarmerDetails,
  handleSelectedAddress,
  NumSyncOTPPopup
} from "../Farmer/NumSyncOTPPopup";
import { useTranslation } from "react-i18next";
import { Container } from "@mui/material";
import { dispatchNotification } from "../../components/Utils";
import { useDispatch } from "react-redux";
import FarmerEditProfileFormComposer from "../../components/FarmerProfileEditFormComposer";
import axiosInstance from "../../services/CreateAxios";
import { getFarmerData } from "../../services/FarmerDbtDetails";
import PropTypes from "prop-types";

// This function is rendering formComposer and syncing with dbt and sending the fetched data from dbt, location apis to formcomposer
function FarmerProfileEditForm({
  isEditProfilePage = false,
  profileData,
  userName = "",
  DBT_ID = null,
  avatarUrl,
  mobileNumber,
  isMobile = false,
}) {
  const authToken = localStorage.getItem("DfsWeb.access-token");
  const dbtLinked = localStorage.getItem("DfsWeb.hasDBTlinked") === "true";
  const [registrationFormDetails, setRegistrationFormDetails] = useState();
  const [dataFromDBT, setDataFromDBT] = useState(dbtLinked);
  const [dbtId, setDbtId] = useState(DBT_ID);
  const [isOtpDialogOpen, setIsOtpDialogOpen] = useState(false);
  const [maskedMobileNumber, setMaskedMobileNumber] = useState("");
  const [errorMessageOTPValidation, setErrorMessageOTPValidation] =
    useState(null);
  const [farmerResponse, setFarmerResponse] = useState();
  const [cancelled, setCancelled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const isInitialLoad = useRef({
    district: false,
    block: false,
    panchayat: false,
    village: false,
  });
  const dispatch = useDispatch();
  const handleCloseOtpDialog = () => {
    setIsOtpDialogOpen(false);
  };

  const { t } = useTranslation();

  const handleDbtSync = async (dbtId) => {
    setLoading(true);
    try {
      await axiosInstance.post(
        "farmer/v1/dbt/_linked?tenantId=br",
        {
          RequestInfo: {
            authToken: null,
          },
          dbtId: dbtId,
          mobileNumber: mobileNumber,
        }
      );
      const farmerData = await getFarmerData(dbtId);
      const farmerDataFromDBT = farmerData?.data;
      if (farmerDataFromDBT?.Individual?.address?.[0]?.villageLG != null) {
        setFarmerResponse(farmerDataFromDBT);
        const farmerDetails = handleFarmerDetails(farmerDataFromDBT);
        setRegistrationFormDetails(farmerDetails);
        const address = handleSelectedAddress(farmerDataFromDBT);
        setSelectedAddress(address);
        setDataFromDBT(true);
        setErrorMessageOTPValidation(null);
        dispatchNotification(
          "success",
          ["FETCHED_DATA"],
          dispatch
        );
      } else {
        dispatchNotification("error", ["notAvaliable"], dispatch);
      }
    } catch (error) {
      setDbtId(dbtId);
      if (error?.response?.data?.dbtMobile) {
        setMaskedMobileNumber(error.response?.data?.dbtMobile || "");
        setIsOtpDialogOpen(true);
      } else if (
        error?.response?.data?.errorMessage.includes("No DBT User Found") 
      ) {
        dispatchNotification(
          "error",
          ["INVALID_DBT_ID"],
          dispatch
        );
      } else {
        dispatchNotification(
          "error",
          ["farmerRegistration.UNEXPECTED_ERROR"],
          dispatch
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const [dropdownValues, setDropdownValues] = useState({
    district: [],
    block: [],
    panchayat: [],
    village: [],
  });

  const [selectedAddress, setSelectedAddress] = useState({
    district: "",
    block: "",
    panchayat: "",
    village: "",
  });

  const {
    data: districtData,
    isLoading: isDistrictLoading,
    isError: isDistrictError,
  } = useDistrict("br");

  const {
    data: blockData,
    isLoading: isBlockLoading,
    isError: isBlockError,
  } = useDistrictBlocks("br", selectedAddress.district, {
    enabled: !!selectedAddress.district,
  });

  const {
    data: panchayatData,
    isLoading: isPanchayatLoading,
    isError: isPanchayatError,
  } = useBlockPanchayats("br", selectedAddress.block, {
    enabled: !!selectedAddress.block,
  });

  const {
    data: villageData,
    isLoading: isVillageLoading,
    isError: isVillageError,
  } = usePanchayatVillages("br", selectedAddress.panchayat, {
    enabled: !!selectedAddress.panchayat,
  });

  // All useEffects also update the corresponding address value
  //setting district dropdown values
  useEffect(() => {
    if (!isDistrictLoading && !isDistrictError && districtData) {
      const formattedDistricts = districtData.map((district) => ({
        id: district.code,
        name: district.name,
      }));

      setDropdownValues((prevValues) => ({
        ...prevValues,
        district: formattedDistricts,
        block: [],
        panchayat: [],
        village: [],
      }));

      setSelectedAddress((prevAddress) => ({
        ...prevAddress,
        block: "",
        panchayat: "",
        village: "",
      }));
    }
  }, [isDistrictLoading, isDistrictError, districtData]);

  //setting block dropdown values
  useEffect(() => {
    if (!isBlockLoading && !isBlockError && blockData) {
      const formattedBlocks = blockData.map((block) => ({
        id: block.code,
        name: block.name,
      }));

      setDropdownValues((prevValues) => ({
        ...prevValues,
        block: formattedBlocks,
        panchayat: [],
        village: [],
      }));

      if (!isInitialLoad.current.block) {
        setSelectedAddress((prevAddress) => ({
          ...prevAddress,
          block: "",
          panchayat: "",
          village: "",
        }));
      }
      isInitialLoad.current = { ...isInitialLoad.current, block: false };
    }
  }, [isBlockLoading, isBlockError, blockData]);

  // setting panchayat dropdown values
  useEffect(() => {
    if (!isPanchayatLoading && !isPanchayatError && panchayatData) {
      const formattedPanchayats = panchayatData.map((panchayat) => ({
        id: panchayat.code,
        name: panchayat.name,
      }));

      setDropdownValues((prevValues) => ({
        ...prevValues,
        panchayat: formattedPanchayats,
        village: [],
      }));
      if (!isInitialLoad.current.panchayat) {
        setSelectedAddress((prevAddress) => ({
          ...prevAddress,
          panchayat: "",
          village: "",
        }));
      }
      isInitialLoad.current = { ...isInitialLoad.current, panchayat: false };
    }
  }, [isPanchayatLoading, isPanchayatError, panchayatData]);

  // setting village dropdown values
  useEffect(() => {
    if (!isVillageLoading && !isVillageError && villageData) {
      const formattedVillages = villageData.map((village) => ({
        id: village.code,
        name: village.name,
      }));
      if (villageData == null || villageData.length == 0) {
        dispatchNotification("error", ["notEligible"], dispatch);
      }
      setDropdownValues((prevValues) => ({
        ...prevValues,
        village: formattedVillages,
      }));
      if (!isInitialLoad.current.village) {
        setSelectedAddress((prevAddress) => ({
          ...prevAddress,
          village: "",
        }));
      }
      isInitialLoad.current = { ...isInitialLoad.current, village: false };
    }
  }, [isVillageLoading, isVillageError, villageData]);

  useEffect(() => {
    if (profileData) {
      const formDetails = handleFarmerDetails(profileData);
      setRegistrationFormDetails(formDetails);
      const address = handleSelectedAddress(profileData);
      setSelectedAddress(address);
      isInitialLoad.current = {
        district: true,
        block: true,
        panchayat: true,
        village: true,
      };
    }
    setDbtId(DBT_ID);
    setCancelled(false);
  }, [profileData, DBT_ID, cancelled]);

  return (
    <Container variant="primary">
      <div style={{ width: "100%" }}>
        <FarmerEditProfileFormComposer
      
          formJson={personalDetails}
          dropdownValues={dropdownValues}
          setSelectedAddress={setSelectedAddress}
          handleDbtSync={handleDbtSync}
          registrationFormDetails={registrationFormDetails}
          dataFromDBT={dataFromDBT}
          dbtId={dbtId}
          authToken={authToken}
          isOtpDialogOpen={isOtpDialogOpen}
          setOtpDialogOpen={setIsOtpDialogOpen}
          selectedAddress={selectedAddress}
          farmerResponse={farmerResponse}
          userName={userName}
          isEditProfilePage={isEditProfilePage}
          imageUrl={avatarUrl}
          setCancelled={setCancelled}
          profileData={profileData}
          dbtLoading={loading}
          isMobile={isMobile}
        />
        {isOtpDialogOpen && <NumSyncOTPPopup
          maskedMobileNumber={maskedMobileNumber}
          open={isOtpDialogOpen}
          otp={otp}
          setOtp={setOtp}
          onClose={handleCloseOtpDialog}
          dbtId={dbtId}
          setErrorMessageOTPValidation={setErrorMessageOTPValidation}
          t={t}
          errorMessageOTPValidation={errorMessageOTPValidation}
          profileData={profileData}
          authToken={authToken}
        />}
      </div>
    </Container>
  );
}

export default FarmerProfileEditForm;

FarmerProfileEditForm.propTypes = {
  mobileNumber: PropTypes.number,
  isMobile: PropTypes.bool,
  isEditProfilePage: PropTypes.bool,
  profileData: PropTypes.object,
  userName: PropTypes.string,
  avatarUrl: PropTypes.string,
  DBT_ID: PropTypes.string,
};
